<template>
  <fragment>
    <div class="fixed-upper-search">
      <v-text-field
        v-model="search"
        :placeholder="selectedSearchType.placeholder"
        color="black"
        clearable
        flat
        hide-details
        outlined
        @keypress.enter="debouncedSearch" />

      <div class="d-flex flex-wrap gap-2 align-center">
        <AppSelect
          :default-item="selectedSearchType"
          :items="searchTypes"
          @resort="setSelectedSearchType" />

        <v-btn
          :loading="showSpinner"
          color="blue"
          class="elevation-0"
          dark
          @click="createNewProduct">
          Create Product
        </v-btn>
      </div>
    </div>

    <Results
      :items="filteredProducts"
      class="scrollable-table"
      @addToSelected="addToSelected"
      @onIntersect="onIntersect" />
  </fragment>
</template>

<script>
import {
  mapMutations, mapState, mapActions, mapGetters,
} from 'vuex';
import {
  debounce, uniqBy, cloneDeep,
} from 'lodash';
import { v4 as uuid } from 'uuid';

import productHeaders from '@/constants/productHeaders';
import { SEARCH_PRODUCT_REF_V3 } from '@/constants/searching/searchingAsyncV2';
import {
  DEBOUNCE_TIME_FOR_SEARCHING, TYPE_CREATE,
} from '@/constants';

import AsyncSearching from '@/mixins/AsyncSearching';

import SearchProducts from '@/services/graphql/searching/searchProducts';

import { parseGetRows } from '@/utils/manageGetRows';

import XRay from '@/utils/xRay/XRay';
const xRay = new XRay();

const SEARCH_TYPES = [
  {
    name: 'Collections',
    request: 'asyncProductsV3',
    dataTest: 'workspace_only_dropdown_option',
    placeholder: 'Find an existing Product record from your Workspace Collections Only',
    params: {
      myCollections: true,
    },
  },
  {
    name: 'Community',
    request: 'asyncCommunityProductsV3',
    dataTest: 'all_community_dropdown_option',
    placeholder: 'Find an existing Product record from all Community Collections',
  },
  {
    name: 'Followed',
    request: 'asyncProductsV3',
    dataTest: 'followed_only_dropdown_option',
    placeholder: 'Find an existing Product record from your Followed Collections Only',
    params: {
      followedOnly: true,
    },
  },
];

export default {
  name: 'ProductCellFieldProductAutocomplete',
  components: {
    AppSelect: () => import('@/components/App/AppSelect'),
    Results: () => import('./ProductCellFieldProductAutocompleteResults'),
  },
  mixins: [AsyncSearching],
  props: {
    col: {
      type: String,
      default: '',
    },
    createdProduct: {
      type: Object,
      default: () => {
      },
    },
    dialog: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    value: {
      type: undefined,
      default: null,
    },
  },
  data() {
    return {
      isProductFromSearch: false,
      products: [],
      scrollID: null,
      search: '',
      selected: null,
      searchTypes: SEARCH_TYPES,
      selectedSearchType: SEARCH_TYPES[0],
    };
  },
  computed: {
    ...mapState(['showSpinner']),
    ...mapState('StoredProducts', ['recentlyAccessedCollection']),
    ...mapState('ProjectDetails', ['detailProjectData']),
    ...mapState('Workspace', ['activeWorkspaceId']),
    ...mapGetters({
      lastProductRendering: 'StoredProducts/lastProductRendering',
    }),
    valueIds() {
      const { value } = this;
      let valueArr = value && Array.isArray(value) ? value : [value];
      return valueArr?.map((e => e?.id)) || [];
    },
    isAccessPreloadingProducts() {
      return this.preloadingProducts.length;
    },
    preloadingProducts() {
      const {
        filteredLastEditedProducts: products,
        filteredRecentlyAccessedCollectionProducts: productCollections,
      } = this;
      return uniqBy([...products, ...productCollections], 'id');
    },
    filteredLastEditedProducts() {
      const { sourceLibraryId = '', valueIds } = this;
      return this.lastProductRendering.reduce((result, current) => {
        const { id = '' } = current || {
        };
        const isSourceLibraryId = current?.libraryId === sourceLibraryId;
        const res = this.parserProduct(current, isSourceLibraryId);
        return res && !valueIds.includes(id) ? [...result, res] : result;
      }, []);
    },
    filteredRecentlyAccessedCollectionProducts() {
      const { sourceLibraryId = '', valueIds } = this;
      const {
        products,
        collectionName,
        libraryId,
        collectionId: collection,
      } = this.recentlyAccessedCollection;
      if (!sourceLibraryId || sourceLibraryId === libraryId) {
        return products.reduce((result, current) => {
          const { id = '' } = current || {
          };
          const isSourceLibraryId = libraryId === sourceLibraryId;
          const res = this.parserProduct(current, isSourceLibraryId);
          return res && !valueIds.includes(id) ? [...result, {
            ...res, collectionName, collection,
          }] : result;
        }, []);
      }
      return [];
    },
    sourceLibraryId() {
      return this.detailProjectData?.sourceLibraryId || '';
    },
    lastProductId() {
      return this.products[this.products.length - 1]?.id;
    },
    filteredProducts() {
      const { value, valueIds } = this;
      if (value) {
        return this.products.filter(item => !valueIds.includes(item.id));
      }
      return this.products;
    },
    // TODO: remove this after the search types are implemented
    textPlaceholder() {
      return 'Find an existing Product record from Collections in the';
    },
    // TODO: remove this after the search types are implemented
    placeholderSearch() {
      if (this.col === 'Product Reference') {
        return this.sourceLibraryId
          ? this.textPlaceholder + ' pre-defined Library'
          : this.textPlaceholder + ' current Workspace';
      }
      return 'Find an existing record';
    },
  },
  watch: {
    createdProduct: {
      deep: true,
      handler(val) {
        this.parseNewProduct(cloneDeep(val));
      },
    },
    value: {
      handler(val) {
        this.selected = val;
      },
      immediate: true,
    },
    dialog: {
      handler(val) {
        const {
          search,
          isAccessPreloadingProducts,
          preloadingProducts,
        } = this;
        if (!val) {
          this.onCloseClear();
          return;
        }
        if (search) {
          this.search = '';
        } else {
          if (isAccessPreloadingProducts) {
            this.isProductFromSearch = false;
            this.products = preloadingProducts;
          }
        }
      },
      immediate: true,
    },
    search: {
      handler() {
        this.debouncedSearch();
      },
    },
  },
  beforeDestroy() {
    this.onCloseClear();
  },
  methods: {
    ...mapMutations({
      changeProductModalVariant: 'Collections/changeProductModalVariant',
      spinner: 'spinner',
    }),
    ...mapActions(['handleError']),
    debouncedSearch: debounce(function() {
      this.searchNewSet();
    }, DEBOUNCE_TIME_FOR_SEARCHING),
    async searchProductRef() {
      this.spinner(true);
      try {
        const LIMIT = 10;
        this.currentRequestId = uuid();
        const { search: query, activeWorkspaceId: workspaceId, scrollID } = this;
        const jsonForRequest = {
          ...(query && {
            query,
          }),
          limit: LIMIT,
          scrollID,
          workspaceId,
          libraryId: workspaceId,
          requestId: this.currentRequestId,
          ...(this.selectedSearchType && this.selectedSearchType.params && {
            ...this.selectedSearchType.params,
          }),
        };

        // TODO: i think this line has no use
        // because we are not using the currentParseMethod anywhere (defined in AsyncSearching mixin)
        this.currentParseMethod = this.compareSearchMatches;

        // If the backend search errors we don't get callback and we endlessly load.
        // Solution for this (also logs warning so we don't miss if it does).
        const reqId = this.currentRequestId;
        const clearSpinnerTimeout = setTimeout(() => {
          if (this.currentRequestId === reqId) {
            console.warn('Search timeout reached before receiving search callback, clearing spinner.');
            this.spinner(false);
          }
        }, 300);

        // subscription for search results
        await this.asyncSearchingSubscription({
          callback: (...args) => {
            clearTimeout(clearSpinnerTimeout);
            this.setSearchResult(...args);
          },
        });
        xRay.startTimeTracker(SEARCH_PRODUCT_REF_V3);
        // actual search request is sent here
        await SearchProducts[this.selectedSearchType.request](jsonForRequest);

      } catch (err) {
        this.spinner(false);
        this.handleError(err);
      }
    },
    addNewProduct(product) {
      const { isProductFromSearch, products, preloadingProducts } = this;
      if (isProductFromSearch) this.products = [product, ...products];
      else this.products = preloadingProducts;
    },
    addToSelected({ id, manufacturer, model, collection } = {
    }) {
      const { multiple } = this;
      const item = {
        id,
        title: `${manufacturer}, ${model}`,
        ...(collection && {
          collection,
        }),
      };
      if (multiple) {
        this.selected = this.selected ? [...this.selected, item] : [item];
      } else {
        this.selected = item;
      }
      this.emitChange();
    },
    compareSearchMatches({ data, searchString } = {
    }) {
      if ((searchString || this.search) && searchString !== this.search) {
        return;
      }
      this.setSearchResult({
        data,
      });
    },
    createNewProduct() {
      this.changeProductModalVariant(TYPE_CREATE);
      this.$emit('setStatusDialog', true);
    },
    emitChange() {
      this.$emit('change', this.selected);
      this.selected = null;
    },
    onCloseClear() {
      this.unsubscribe();
      this.spinner(false);
    },
    onIntersect({ entries, isIntersecting } = {
    }) {
      if (isIntersecting) {
        const productId = entries[0].target.id;
        if (this.scrollID && productId === this.lastProductId) {
          this.searchProductRef();
        }
      }
    },
    parseNewProduct(product) {
      const {
        fields = [], collectionId = '', collectionName
        = '', libraryId = '',
      } = product || {
      };
      const parsedProduct = parseGetRows(fields);
      const objProduct = {
        collectionId,
        collectionName,
        libraryId,
        ...parsedProduct,
      };
      const mappedProduct = this.parserProduct(objProduct, true);
      this.addNewProduct(mappedProduct);
      this.addToSelected(mappedProduct);
    },
    parserProduct(product, isSourceLibraryId) {
      const { IMAGE, PRODUCT_TYPE, MODEL, MANUFACTURER, DESCRIPTION } = productHeaders;
      const { VERIFIED, CHILD_VERIFIED, VERIFIED_PAGE_ID, VERIFIED_PAGE_NAME } = productHeaders;
      const { sourceLibraryId = '' } = this;
      const {
        collectionName = '',
        id = '',
        [IMAGE]: attachment = [],
        [PRODUCT_TYPE]: productType = [],
        [MODEL]: model = '',
        [MANUFACTURER]: manufacturer = '',
        [DESCRIPTION]: shortDescription = '',
        [VERIFIED]: isVerified = false,
        [CHILD_VERIFIED]: childVerified = '',
        [VERIFIED_PAGE_ID]: verifiedPageId = '',
        [VERIFIED_PAGE_NAME]: verifiedPageName = '',
        collectionId: collection = '',
      } = product || {
      };
      const isPassProductSourceLibrary = !sourceLibraryId || isSourceLibraryId;
      const parsedProduct = {
        id,
        attachment,
        productType,
        model,
        collectionName,
        manufacturer,
        shortDescription,
        collection,
        isVerified,
        childVerified,
        verifiedPageId,
        verifiedPageName,
      };
      return !isPassProductSourceLibrary ? null : product.hasOwnProperty('SK') ? parsedProduct : product;
    },
    searchNewSet() {
      this.resetSearch();
      this.searchProductRef();
    },
    resetSearch() {
      this.scrollID = null;
      this.isProductFromSearch = true;
      this.products = [];
    },
    setSearchResult({ data } = {
    }) {
      this.spinner(false);
      const { data: dataProducts, scrollID, queryName } = data || {
      };
      xRay.stopTimeTracker(queryName);
      xRay.sendSegmentsToAws();
      this.products = this.scrollID ? [...this.products, ...dataProducts] : dataProducts;
      this.products = this.lodash.uniqBy(this.products, 'id');
      this.scrollID = scrollID;
    },
    setSelectedSearchType(item) {
      this.selectedSearchType = item;
      this.resetSearch();
      if (this.search) {
        this.searchNewSet();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.fixed-upper-search {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  flex: 0 0 auto;
}

.scrollable-table {
  flex: 1 1 auto;
  min-height: 0;
}
</style>
